/**
 * iter1031 — BHI Dashboard Sidebar (preview)
 *
 * Sticky vertical navigation, 5 логически групи + search box.
 * Smooth-scroll до секции по `data-testid="section-…"` в правата колона.
 * Active item се маркира с Intersection Observer (mock в preview).
 */
import React, { useState, useMemo } from "react";
import {
  Search, Brain, Utensils, Gauge, FileText, ClipboardList, Stethoscope,
  Dna, Heart, Activity, Target, Apple, Timer, FlaskConical, Camera,
  Download, ShieldCheck, Sparkles, ChevronRight, Map, Bell,
  Hourglass, ShieldAlert, ScanLine, BookOpen, Zap, Menu, X, CalendarCheck,
} from "lucide-react";

const GROUPS = [
  {
    id: "overview",
    label_bg: "Обзор",
    items: [
      { id: "patient-journey",   label_bg: "Твоят път в BHI",         icon: Map,       testid: "section-patient-journey" },
      { id: "daily-habits",      label_bg: "⚡ Ежедневни навици",     icon: Zap,       testid: "bhi-daily-companion" },
      { id: "ai-summary",        label_bg: "AI резюме",               icon: Brain,     testid: "section-ai-summary" },
      { id: "nutritional-fp",    label_bg: "Нутриционен отпечатък",   icon: Utensils,  testid: "section-nutritional-fingerprint" },
      { id: "top-stats",         label_bg: "Ключови показатели",      icon: Gauge,     testid: "section-top-stats" },
      { id: "nudge-feed",        label_bg: "Действия за проследяване",icon: Bell,      testid: "section-nudge-feed" },
      { id: "plate-bridge",      label_bg: "Мост към чинията",        icon: Apple,     testid: "section-plate-bridge" },
    ],
  },
  {
    id: "data",
    label_bg: "Моите данни",
    items: [
      { id: "my-data",           label_bg: "Качване на данни",        icon: FileText,      testid: "section-my-data" },
      { id: "doc-history",       label_bg: "История на документите",  icon: ClipboardList, testid: "section-doc-history" },
      { id: "auto-diagnosis",    label_bg: "Автоматична диагноза",    icon: Stethoscope,   testid: "section-auto-diagnosis" },
      { id: "bc-subtype",        label_bg: "BC молекулярен подтип",   icon: Dna,           testid: "section-bc-subtype" },
      { id: "speclab",           label_bg: "SpecLab (DUTCH / ДНК)",   icon: FlaskConical,  testid: "section-speclab" },
    ],
  },
  {
    id: "assess-observe",
    label_bg: "🪞 Оценка & 🔬 Наблюдение",
    items: [
      { id: "hereditary",        label_bg: "Наследствен риск (DKG)",  icon: Dna,         testid: "section-hereditary" },
      { id: "screening-plan",    label_bg: "Моят план за проверките",  icon: CalendarCheck, testid: "section-screening-plan" },
      { id: "prevention-path",   label_bg: "Пътят към превенция",     icon: Target,      testid: "section-prevention-path" },
      { id: "reference-context", label_bg: "Данни от твоя профил",    icon: ShieldAlert, testid: "section-reference-context" },
      { id: "biology-observe",   label_bg: "Biology Dashboard",       icon: Sparkles,    testid: "section-biology-observe" },
      { id: "density",           label_bg: "Плътност (BI-RADS)",      icon: ScanLine,    testid: "section-density" },
      { id: "tissue-age",        label_bg: "Възраст на тъканта",      icon: Hourglass,   testid: "section-tissue-age" },
      { id: "metabolic",         label_bg: "Метаболитно",             icon: Activity,    testid: "section-metabolic" },
    ],
  },
  {
    id: "understand",
    label_bg: "🧬 Разбиране (BDI · BTRE · BPP)",
    items: [
      { id: "molecular",         label_bg: "Молекулен профил",        icon: Dna,       testid: "section-molecular" },
      { id: "geno-lab",          label_bg: "BDI · Geno Lab (23 заб.)",icon: Dna,       testid: "section-geno-lab" },
      { id: "living-metabolism", label_bg: "Жив метаболизъм",         icon: Heart,     testid: "section-living-metabolism" },
      { id: "kegg-activity",     label_bg: "KEGG активност",          icon: Activity,  testid: "section-kegg-activity" },
    ],
  },
  {
    id: "plan-act-support",
    label_bg: "🌿 План, Действие & Подкрепа",
    items: [
      { id: "tissue-plan",         label_bg: "План за тъканта",         icon: Timer,      testid: "section-tissue-plan" },
      { id: "prevention-program",  label_bg: "Prevention Program",      icon: Target,     testid: "section-prevention-program" },
      { id: "simulators",          label_bg: "Симулатори (What-if)",    icon: Sparkles,   testid: "section-simulators" },
      { id: "coach",               label_bg: "Health Coach",            icon: Heart,      testid: "section-coach" },
      { id: "interventions",       label_bg: "Интервенции",             icon: Apple,      testid: "section-interventions" },
      { id: "ecosystem",           label_bg: "Ecosystem Map",           icon: BookOpen,   testid: "section-ecosystem" },
    ],
  },
];

export default function BhiDashboardSidebar({ isDark = false, activeId = "ai-summary", onNavigate, orderMap = null }) {
  const sidebarBg = isDark ? "bg-[hsl(222,47%,8%)] border-[hsl(223,47%,15%)]"
                           : "bg-white border-slate-200";

  return (
    <aside
      className={`sticky top-4 self-start w-64 shrink-0 rounded-2xl border ${sidebarBg} p-4 max-h-[calc(100vh-2rem)] overflow-y-auto hidden lg:block`}
      data-testid="bhi-dashboard-sidebar"
    >
      <SidebarBody isDark={isDark} activeId={activeId} onNavigate={onNavigate} orderMap={orderMap} />
    </aside>
  );
}

/**
 * iter1034 — Mobile drawer variant. Floating hamburger button (bottom-right)
 * opens a slide-in drawer that mirrors the desktop sidebar. Auto-closes on
 * navigate. Hidden on ≥ lg (where the sticky sidebar is shown instead).
 */
export function BhiDashboardMobileDrawer({ isDark = false, activeId, onNavigate, orderMap = null }) {
  const [open, setOpen] = useState(false);

  const handleNav = (item) => {
    onNavigate?.(item);
    setOpen(false);
  };

  const drawerBg = isDark ? "bg-[hsl(222,47%,8%)] border-[hsl(223,47%,15%)]"
                          : "bg-white border-slate-200";

  return (
    <>
      {/* Floating hamburger — visible only on <lg */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Отвори навигация"
        data-testid="bhi-mobile-drawer-open"
        className="lg:hidden fixed bottom-5 right-5 z-40 w-12 h-12 rounded-full bg-emerald-600 text-white shadow-lg flex items-center justify-center hover:bg-emerald-700 active:scale-95 transition"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Backdrop + slide-in drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="bhi-mobile-drawer">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className={`absolute inset-y-0 left-0 w-72 border-r ${drawerBg} p-4 overflow-y-auto shadow-2xl animate-in slide-in-from-left duration-200`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-emerald-300" : "text-emerald-600"}`}>
                BHI Навигация
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Затвори навигация"
                data-testid="bhi-mobile-drawer-close"
                className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-500 hover:bg-slate-100"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <SidebarBody isDark={isDark} activeId={activeId} onNavigate={handleNav} />
          </div>
        </div>
      )}
    </>
  );
}

/** Shared body — same content in both sticky-sidebar and mobile-drawer modes. */
function SidebarBody({ isDark, activeId, onNavigate, orderMap }) {
  const [query, setQuery] = useState("");

  // iter1016 — менюто следва реда, зададен от админ (по data-testid).
  const groups = useMemo(() => {
    if (!orderMap) return GROUPS;
    return GROUPS.map((g) => ({
      ...g,
      items: [...g.items].sort(
        (a, b) => (orderMap[a.testid] ?? 9990) - (orderMap[b.testid] ?? 9990)),
    }));
  }, [orderMap]);

  const filtered = useMemo(() => {
    if (!query.trim()) return groups;
    const q = query.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.label_bg.toLowerCase().includes(q) ||
            g.label_bg.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [query, groups]);

  const inputBg    = isDark ? "bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400";
  const groupHdr   = isDark ? "text-slate-500" : "text-slate-400";
  const itemBase   = isDark ? "text-slate-300 hover:bg-slate-800/60 hover:text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
  const itemActive = isDark ? "bg-emerald-500/15 text-emerald-300 border-l-2 border-emerald-400"
                            : "bg-emerald-50 text-emerald-700 border-l-2 border-emerald-500";

  return (
    <>
      {/* Search */}
      <div className="relative mb-4">
        <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Търсене…"
          className={`w-full text-xs pl-8 pr-2 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${inputBg}`}
          data-testid="bhi-dashboard-search"
        />
      </div>

      {/* Groups */}
      <nav className="space-y-4">
        {filtered.map((g) => (
          <div key={g.id}>
            <div className={`px-2 mb-1.5 text-[10px] font-bold uppercase tracking-wider ${groupHdr}`}>
              {g.label_bg}
            </div>
            <ul className="space-y-0.5">
              {g.items.map((it) => {
                const Icon = it.icon;
                const isActive = activeId === it.id;
                return (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => onNavigate?.(it)}
                      data-testid={`bhi-dash-nav-${it.id}`}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs font-medium rounded-md transition-all
                        ${isActive ? itemActive : itemBase}`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate flex-1 text-left">{it.label_bg}</span>
                      {isActive && <ChevronRight className="w-3 h-3 opacity-70" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className={`text-xs px-2 py-4 text-center ${groupHdr}`}>
            Няма съвпадения за „{query}"
          </div>
        )}
      </nav>

      {/* Footer badge — view toggle hint */}
      <div className={`mt-6 pt-4 border-t ${isDark ? "border-slate-800" : "border-slate-100"}`}>
        <div className={`text-[10px] ${groupHdr} px-2`}>
          Активен изглед: <span className="font-semibold text-emerald-500">Dashboard</span>
        </div>
      </div>
    </>
  );
}

export { GROUPS as BHI_DASHBOARD_GROUPS };
